import React from "react";

import { PlusGrayIcon } from "../../../CommonUI/Icon/PlusGrayIcon";

import { ObserveState } from "../../../CommonUI/StateManagement/ObserveState";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import {
    RuleDesignerRender,
    RuleEditorInTester,
    RuleEditorRender,
    RuleTableEmptyAddColumn,
    RuleTableEmptyColumn,
    RuleTableEmptyIndexColumn,
    RuleToolbarRender,
} from "../../DesignUI/CommonStyles";

import { Notification } from "../../../CommonUI/BuilderUI/Notification";
import { IntersectionRuleConfiguration } from "../../../rule-engine-core/Rule/Intersection/IntersectionRuleConfiguration";
import { LinkingDataDialog } from "../../DesignUI/LinkingDataDialog";
import { IntersectionRuleEditContext } from "../../../rule-engine-core/Rule/Intersection/IntersectionRuleEditContext";
import { IntersectionRuleHeader } from "./IntersectionRuleHeader";
import { IntersectionRuleRow } from "./IntersectionRuleRow";
import { DataTypePicker } from "../../../CommonUI/DataTypePicker/DataTypePicker";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { ValidationInformation } from "../../DesignUI/ValidationInformation";
import { TestContext } from "../../../CommonUI/Testing/TestContext";
import { TestResult } from "../../../CommonUI/Testing/TestResult";

interface IIntersectionRuleDesigner {
    editContext: IntersectionRuleEditContext;
    ruleDefinition: IntersectionRuleConfiguration;
}

export const IntersectionRuleDesigner = (props: IIntersectionRuleDesigner) => {
    const { ruleDefinition, editContext } = props;
    const testContext = React.useContext(TestContext);

    const onAddNewYAxisRule = () => {
        ruleDefinition.addYAxisExpression();
        StateManager.propertyChanged(ruleDefinition, "yaxisExpressions");
    };

    if (testContext) {
        return (
            <RuleEditorInTester>
                <RuleEditorRender
                    columnCount={ruleDefinition.xaxisExpressions.length + 1}
                    columnsGap={6}
                    rowsGap={6}
                >
                    <IntersectionRuleHeader
                        ruleDefinition={ruleDefinition}
                        expressions={ruleDefinition.xaxisExpressions}
                    />

                    <>
                        {ruleDefinition.yaxisExpressions.map((yaxisExp, index) => (
                            <IntersectionRuleRow
                                yexpression={yaxisExp}
                                expressions={ruleDefinition.xaxisExpressions}
                                y={index}
                                key={yaxisExp._id}
                                ruleDefinition={ruleDefinition}
                            />
                        ))}
                    </>
                    <RuleTableEmptyIndexColumn
                        data-testid="btn-intersectionrule-row-add"
                        disabled={!ruleDefinition.canChangeOutputs}
                    ></RuleTableEmptyIndexColumn>

                    <RuleTableEmptyColumn />
                    <div />
                    {ruleDefinition.xaxisExpressions.map((_, idx) => (
                        <RuleTableEmptyColumn key={idx} />
                    ))}
                    <RuleTableEmptyAddColumn />
                </RuleEditorRender>
                <TestResult executableId={ruleDefinition._id} />
            </RuleEditorInTester>
        );
    }

    return (
        <RuleDesignerRender>
            <RuleToolbarRender>
                {/* need idatadef type picker */}

                <DataTypePicker
                    variant={"header"}
                    dataDef={ruleDefinition.resultType}
                    readonly={!ruleDefinition.canChangeOutputs}
                    width={"150px"}
                    label={"Result:"}
                    updateCallback={(newType: IDataDefinition) => {
                        ruleDefinition.setTypeResults(newType);
                        StateManager.propertyChanged(ruleDefinition, "resultType");
                    }}
                />
                {/* <SettingButton /> */}
            </RuleToolbarRender>
            <ObserveState
                listenTo={ruleDefinition}
                properties={["xaxisExpressions", "resultType"]}
                control={() => {
                    return (
                        <RuleEditorRender columnCount={ruleDefinition.xaxisExpressions.length + 1}>
                            <IntersectionRuleHeader
                                ruleDefinition={ruleDefinition}
                                expressions={ruleDefinition.xaxisExpressions}
                            />
                            <ObserveState
                                listenTo={ruleDefinition}
                                properties={["yaxisExpressions"]}
                                control={() => {
                                    return (
                                        <>
                                            {ruleDefinition.yaxisExpressions.map((yaxisExp, index) => (
                                                <IntersectionRuleRow
                                                    yexpression={yaxisExp}
                                                    expressions={ruleDefinition.xaxisExpressions}
                                                    y={index}
                                                    key={yaxisExp._id}
                                                    ruleDefinition={ruleDefinition}
                                                />
                                            ))}
                                        </>
                                    );
                                }}
                            />
                            <ObserveState
                                listenTo={ruleDefinition}
                                properties={["canChangeOutputs"]}
                                control={() => (
                                    <RuleTableEmptyIndexColumn
                                        onClick={onAddNewYAxisRule}
                                        data-testid="btn-intersectionrule-row-add"
                                        disabled={!ruleDefinition.canChangeOutputs}
                                    >
                                        <PlusGrayIcon />
                                    </RuleTableEmptyIndexColumn>
                                )}
                            />

                            <RuleTableEmptyColumn />
                            <div />
                            {ruleDefinition.xaxisExpressions.map((_, idx) => (
                                <RuleTableEmptyColumn key={idx} />
                            ))}
                            <RuleTableEmptyAddColumn />
                        </RuleEditorRender>
                    );
                }}
            />

            <ObserveState
                listenTo={ruleDefinition}
                control={() => <ValidationInformation provider={ruleDefinition} />}
            />

            <Notification editContext={editContext} />

            <LinkingDataDialog editContext={editContext} />
        </RuleDesignerRender>
    );
};
